

class ApiService {
  constructor() {
    // Base URL from env var — see api.config.js for setup instructions.
    // In local dev, leave REACT_APP_API_BASE_URL unset so requests go to the
    // same origin and the CRA proxy (package.json "proxy") forwards them to the backend.
    this.baseURL = process.env.REACT_APP_API_BASE_URL || "";
    this.timeout = 10000; // 10 seconds
  }

  // Generic HTTP request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const defaultHeaders = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    // Add authorization header if token exists
    const token = this.getAuthToken();
    if (token) {
      defaultHeaders["Authorization"] = `Bearer ${token}`;
    }

    const config = {
      method: "GET",
      timeout: this.timeout,
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data,
        status: response.status,
        headers: response.headers,
      };
    } catch (error) {
      console.error("API Request failed:", error);

      if (error.name === "AbortError") {
        throw new Error("Request timeout");
      }

      return {
        success: false,
        error: error.message || "Network error",
        status: error.status || 0,
      };
    }
  }

  // GET request
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    return this.request(url, {
      method: "GET",
    });
  }

  // POST request
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // PUT request
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // PATCH request
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, {
      method: "DELETE",
    });
  }

  // Authentication methods
  setAuthToken(token) {
    localStorage.setItem("auth_token", token);
  }

  getAuthToken() {
    return localStorage.getItem("auth_token");
  }

  removeAuthToken() {
    localStorage.removeItem("auth_token");
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getAuthToken();
  }
}

export default ApiService;
